import type {
  ProposalChangeLine,
  ProposalCollaborationEnvelope,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import {
  canonicalProposalCollectionItem,
  proposalSemanticJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";
import {
  proposalCollectionItemConflictKeyAspect,
  readProposalCollectionItemAspectValue,
} from "work/proposals/negotiation/proposal-collection-item-revert";
import { proposalCollectionItemHasIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";

/**
 * True when a change line's unsent work ends on the value the counterparty already holds, so that
 * publishing it would send nothing (#5550).
 *
 * A reviewer removes a team member and the leader adds the same member back: two saved changes, by two
 * people, so the local queue never composed them away. The line then carries the counterparty's value
 * as `previousValue` — the member — and the projection holds the same member. Offered as a change, it
 * read as "updated" from that member to that member, came with a Revert toggle that restored nothing,
 * and armed Submit by itself.
 *
 * The server settles such a line at the next submission (`NetZeroSettlements` in
 * `ProposalCollaborationEnvelopeService`). This answers the same question for the window before that
 * submission, from what the line and the projection both say.
 *
 * Values are compared in their canonical ledger form (`canonicalProposalCollectionItem`), because the
 * projection carries display fields — a member's name, a document's info — that the line's value never
 * does. A line with no previous value, or whose item or column the line cannot state, is never read as
 * sending nothing: silence is not evidence that nothing changed.
 */
export function proposalChangeLineSendsNothing(
  envelope: ProposalCollaborationEnvelope,
  line: ProposalChangeLine,
): boolean {
  if (line.previousValueExists !== true) return false;

  const atPath = readProposalOperationValue(envelope.projection, line.path);
  if (!line.itemId) return proposalSemanticJsonValuesEqual(line.previousValue, atPath);

  if (!Array.isArray(atPath)) return false;
  const itemId = line.itemId;
  const item: unknown = atPath.find((entry) => proposalCollectionItemHasIdentity(entry, itemId));
  if (item === undefined) return false;

  const aspect = proposalCollectionItemConflictKeyAspect(line.path, itemId, line.conflictKey);
  if (!aspect) {
    return proposalSemanticJsonValuesEqual(
      canonicalProposalCollectionItem(line.path, line.previousValue),
      canonicalProposalCollectionItem(line.path, item),
    );
  }

  const previous = readProposalCollectionItemAspectValue(line.conflictKey, line.previousValue, aspect);
  if (!previous.exists) return false;
  const current = readProposalCollectionItemAspectValue(line.conflictKey, item, aspect);
  return proposalSemanticJsonValuesEqual(
    canonicalAspectValue(line.path, aspect, previous.value),
    canonicalAspectValue(line.path, aspect, current.value),
  );
}

/** One column of a collection item in its ledger form, e.g. a fee category's `teamMember` as ids only. */
function canonicalAspectValue(path: string, aspect: string, value: unknown): unknown {
  const canonical = canonicalProposalCollectionItem(path, { [aspect]: value });
  return typeof canonical === "object" && canonical !== null && Object.prototype.hasOwnProperty.call(canonical, aspect)
    ? (canonical as Record<string, unknown>)[aspect]
    : value;
}
